/**
 * Active-org context (multi-tenancy Phase F).
 *
 * Holds the org the user is currently acting within and pushes it into the
 * api-client as the `X-Org-Id` header (lib/api-client.setOrgId) so every
 * `/v1/iot/*` + `/v1/twin/*` call is scoped to that org server-side. The
 * memberships come from `/v1/platform/auth/me` via the auth provider
 * (user.orgMemberships); this provider only owns the *selection* + the
 * header wiring + the role/platform-admin derivation the UI gates on.
 *
 * Selection rules: persist the chosen org in localStorage; on load, use it
 * if the user still belongs to it, else fall back to their first
 * membership. Single-membership users (the pre-cutover norm) resolve
 * transparently — the header is set but the backend would also resolve
 * their sole org without it, so this is non-breaking.
 */

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/lib/auth";
import { setOrgId } from "@/lib/api-client";
import { linkedOrgOf, orgParamOf, type SuiteProduct } from "@/lib/suite";

const STORAGE_KEY = "divineiot:active-org";
// An org a link from another suite product named (?org=<uuid>), waiting for the user's orgs: kept for this tab, so it
// survives the sign-in round trip, and promoted to STORAGE_KEY only once their orgs hold it.
const LINKED_KEY = "divineiot:linked-org";

function readLinked(): string | null {
  try {
    return window.sessionStorage.getItem(LINKED_KEY);
  } catch {
    return null;
  }
}

function writeLinked(org: string | null) {
  try {
    if (org) window.sessionStorage.setItem(LINKED_KEY, org);
    else window.sessionStorage.removeItem(LINKED_KEY);
  } catch {
    /* ignore private mode */
  }
}

export interface OrgMembership {
  orgId: string;
  orgName: string;
  roles: string[];
  /** org_branding.vendor_org_id — non-null when the foundry came via a vendor
   *  (Versatile); drives the dual Sahas+Versatile logo. */
  viaVendorOrgId?: string | null;
  /** orgs.name of the vendor — the co-brand wordmark, generic per vendor. */
  vendorName?: string | null;
  /** True when this org is itself a vendor (a vendor_orgs row) — gates the
   *  vendor-scoped nav + Fleet landing. */
  isVendor?: boolean;
  /** The role's capability bundle (from /auth/me permissions[]) — drives can(perm). */
  permissions: string[];
  /** The suite products the org holds, Twin first, with the member's role in each (lib/suite). */
  products?: SuiteProduct[];
}

export interface OrgState {
  orgs: OrgMembership[];
  activeOrgId: string | null;
  activeOrg: OrgMembership | null;
  /** Active org's role (admin | member | viewer), or null when no org. */
  role: string | null;
  /** Capability helpers the UI gates on (mirror backend RBAC tiers). */
  canWrite: boolean; // member or admin
  canAdmin: boolean; // admin (manage org + members)
  /** Fine-grained permission check — true iff the active org's role grants
   *  `perm` (from /auth/me permissions[]). The permission-model successor to
   *  canWrite/canAdmin; gate new edit/admin/capture UI on this (e.g.
   *  can("capture.write"), can("gateway.commission")). */
  can: (perm: string) => boolean;
  isPlatformAdmin: boolean;
  /** Vendor-admin — admins a vendor_orgs parent (Versatile). Gates the
   *  vendor-portal nav + route. */
  isVendorAdmin: boolean;
  /** Switch the active org (persisted + pushed to the api-client header). */
  setActiveOrg: (orgId: string) => void;
}

const OrgContext = createContext<OrgState | null>(null);

// legacyCan maps a permission onto the pre-RBAC role tiers (admin > member >
// viewer). It's a FALLBACK used only when the backend hasn't started emitting
// permissions[] yet (openapi: "ABSENT on older servers → derive from role") — so
// can() degrades to role-based gating instead of hiding every gated control
// during the rollout window. Once every deployed backend emits permissions[],
// this path is dead (perms.length is always > 0).
function legacyCan(perm: string, role: string | null): boolean {
  switch (perm) {
    case "data.read":
      return role === "admin" || role === "member" || role === "viewer";
    case "org.admin":
    case "org.owner":
      return role === "admin" || role === "owner";
    default: // capture.write / gateway.commission / model.write / *.write
      return role === "admin" || role === "member";
  }
}

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const orgs = useMemo<OrgMembership[]>(
    () => user?.orgMemberships ?? [],
    [user?.orgMemberships],
  );

  const [activeOrgId, setActiveOrgId] = useState<string | null>(() => {
    try {
      return window.localStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // A link from another suite product names its org (?org=<uuid>). It waits for the user's orgs (linkedOrgOf), so a
  // link to an org they are not in never replaces their stored choice; meanwhile it wins over that choice where they
  // are in it. The address loses the param through the router, so a page that copies the query (Admin's tab) cannot
  // put it back.
  const location = useLocation();
  const navigate = useNavigate();
  const linkedParam = orgParamOf(location.search);
  const [waitingOrgId, setWaitingOrgId] = useState<string | null>(readLinked);
  const linkedOrgId = linkedParam.org ?? waitingOrgId;
  useEffect(() => {
    if (!linkedParam.present) return;
    if (linkedParam.org) {
      writeLinked(linkedParam.org);
      setWaitingOrgId(linkedParam.org);
    }
    navigate(
      { pathname: location.pathname, search: linkedParam.rest, hash: location.hash },
      { replace: true, state: location.state },
    );
  }, [linkedParam.present, linkedParam.org, linkedParam.rest, location.pathname, location.hash, location.state, navigate]);
  useEffect(() => {
    const next = linkedOrgOf(linkedOrgId, orgs.map((o) => o.orgId));
    if (next === null || next === "wait") return;
    if (next === "promote" && linkedOrgId) {
      try {
        window.localStorage.setItem(STORAGE_KEY, linkedOrgId);
      } catch {
        /* ignore quota / private-mode */
      }
      setActiveOrgId(linkedOrgId);
    }
    writeLinked(null);
    setWaitingOrgId(null);
  }, [linkedOrgId, orgs]);

  // Resolve the effective org during render: a linked org the user is in, else the stored/selected org if it's still
  // a valid membership, else the first membership, else null (org-less or pre-/auth/me).
  const effectiveOrgId = useMemo<string | null>(() => {
    if (orgs.length === 0) return null;
    if (linkedOrgId && orgs.some((o) => o.orgId === linkedOrgId)) return linkedOrgId;
    const valid = activeOrgId && orgs.some((o) => o.orgId === activeOrgId);
    return valid ? activeOrgId! : orgs[0].orgId;
  }, [orgs, activeOrgId, linkedOrgId]);

  // Push X-Org-Id synchronously during render — NOT in an effect. React
  // runs effects child-first, so an effect here would set the header only
  // AFTER the data providers nested below have already fetched with no
  // X-Org-Id (→ 400 org_required, which the QueryClient won't retry). A
  // render-time assignment runs parent-first, so the header is in place
  // before any descendant query mounts. Idempotent module write — safe
  // during render. No need to write the resolved default back to state:
  // `value` + the header both derive from effectiveOrgId, which recomputes
  // each render; activeOrgId state only captures explicit user switches.
  setOrgId(effectiveOrgId);

  // Reset the React Query cache whenever the active org actually changes, so no
  // screen serves the previous tenant's cached data (the bug: a freshly created
  // or switched org showed stale "couldn't load bindings"/"unknown" until a
  // hard refresh — every query key was org-agnostic and nothing invalidated on
  // switch). The X-Org-Id header is set synchronously above, so the refetches
  // triggered here go out under the NEW org. Initialising the ref to the first
  // effectiveOrgId makes the initial mount a no-op — this fires only on a
  // genuine switch/create, removing the need for a manual reload.
  const queryClient = useQueryClient();
  const prevOrgIdRef = useRef<string | null>(effectiveOrgId);
  useEffect(() => {
    if (prevOrgIdRef.current === effectiveOrgId) return;
    prevOrgIdRef.current = effectiveOrgId;
    void queryClient.resetQueries();
  }, [effectiveOrgId, queryClient]);

  const setActiveOrg = (orgId: string) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, orgId);
    } catch {
      /* ignore quota / private-mode */
    }
    setOrgId(orgId);
    setActiveOrgId(orgId);
  };

  const value = useMemo<OrgState>(() => {
    const activeOrg = orgs.find((o) => o.orgId === effectiveOrgId) ?? null;
    const role = activeOrg?.roles[0] ?? null;
    // Backend-emitted permissions[] are authoritative; fall back to the legacy
    // role tiers only when an older backend omits them (so the UI never goes
    // fully dark mid-rollout — see legacyCan).
    const canFn = (perm: string) => {
      const perms = activeOrg?.permissions ?? [];
      return perms.length > 0 ? perms.includes(perm) : legacyCan(perm, role);
    };
    return {
      orgs,
      activeOrgId: effectiveOrgId,
      activeOrg,
      role,
      canWrite: role === "admin" || role === "member",
      // Permission-based, NOT the brittle role==="admin" — true for admin AND
      // owner. Gates the Team tab + every org-admin surface, so an owner is no
      // longer locked out of team management.
      canAdmin: canFn("org.admin"),
      can: canFn,
      isPlatformAdmin: !!user?.isPlatformAdmin,
      isVendorAdmin: !!user?.isVendorAdmin,
      setActiveOrg,
    };
  }, [orgs, effectiveOrgId, user?.isPlatformAdmin, user?.isVendorAdmin]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) {
    throw new Error("useOrg must be used inside <OrgProvider>");
  }
  return ctx;
}
